import { ChangeEvent, FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Checkbox, FormControlLabel } from '@mui/material';

import { IMedicationInquiry } from '@interfaces/medications';
import { ISupplementInquiry } from '@interfaces/supplements';
import {
  inquiriesInquiryToShareSelector,
  inquiriesShareSelectedSupplementsSelector,
} from '@store/features/inquiries/share/selectors';

import './style.scss';

interface IProps {
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
}

const SelectSupplements: FC<IProps> = ({ onChange }: IProps): JSX.Element | null => {
  const { t } = useTranslation();

  const inquiryToShare = useSelector(inquiriesInquiryToShareSelector);
  const shareSelectedSupplements = useSelector(inquiriesShareSelectedSupplementsSelector);

  const shareInquiryItems = useMemo(() => {
    if (!inquiryToShare) {
      return [];
    }
    if (inquiryToShare.isSupplementary) {
      return inquiryToShare.supplementInquiriesDto.filter(item => item.isActive);
    }
    return inquiryToShare.medicationInquiriesDto;
  }, [inquiryToShare]);

  const areAllSelected = useMemo(() => {
    if (!inquiryToShare) {
      return false;
    }
    if (inquiryToShare.isSupplementary) {
      return (shareInquiryItems as ISupplementInquiry[]).every(item =>
        shareSelectedSupplements.includes(item.supplementInquiryId),
      );
    }
    return (shareInquiryItems as IMedicationInquiry[]).every(item =>
      shareSelectedSupplements.includes(item.medicationInquiryId),
    );
  }, [inquiryToShare, shareInquiryItems, shareSelectedSupplements]);

  const itemsToRender = useMemo(() => {
    if (!inquiryToShare) {
      return [];
    }
    if (inquiryToShare.isSupplementary) {
      return (shareInquiryItems as ISupplementInquiry[]).map(item => ({
        id: item.supplementInquiryId,
        name: item.name,
      }));
    }
    return (shareInquiryItems as IMedicationInquiry[]).map(item => ({
      id: item.medicationInquiryId,
      name: item.name,
    }));
  }, [inquiryToShare, shareInquiryItems]);

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (onChange) {
        onChange(event);
      }
    },
    [onChange],
  );

  if (!inquiryToShare) {
    return null;
  }

  return (
    <div className="select-supplements">
      <div className="select-supplements__body">
        <FormControlLabel
          className="select-supplements__checkbox"
          control={
            <Checkbox
              name="selected-supplements"
              checked={areAllSelected}
              onChange={handleChange}
              value="all"
            />
          }
          label={
            inquiryToShare?.isSupplementary
              ? t('shareResponse.allSupplements')
              : t('shareResponse.allMedications')
          }
        />

        {itemsToRender.map(item => (
          <FormControlLabel
            className="select-supplements__checkbox"
            key={item.id}
            control={
              <Checkbox
                name="selected-supplements"
                checked={shareSelectedSupplements.includes(item.id)}
                onChange={handleChange}
                value={item.id}
              />
            }
            label={item.name}
          />
        ))}
      </div>
    </div>
  );
};

export default SelectSupplements;
